import { FC } from 'react';
import { AxiosResponse, isAxiosError } from 'axios';
import { ZodError } from 'zod/v3';
import styled from 'styled-components';
import { Button, Flex, message as Message, Tag } from 'antd';
import * as Icon from 'react-feather';
import { colorGray } from '@atlas-frontend/design-tokens';
import { CodeBlock, isObject } from '@atlas-frontend/sdk';
import { colorRed } from './tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InternalErrorDisplay.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('error-display');
addTranslations(translations);

export interface ResponseErrorDisplayProps {
  error: unknown;
}

export const InternalErrorDisplay: FC<ResponseErrorDisplayProps> = (props) => {
  const { error } = props;

  if (!(error instanceof Error)) {
    return <ErrorDisplayInner layer="ui" message={String(error)} />;
  }
  if (error instanceof ZodError) {
    return (
      <ErrorDisplayInner
        layer="ui"
        message={error.name}
        trace={`${error.message}\n\n${error.stack}`}
      />
    );
  }
  if (!isAxiosError(error)) {
    return (
      <ErrorDisplayInner
        layer="ui"
        message={error.message}
        trace={error.stack}
      />
    );
  }

  return (
    <ErrorDisplayInner
      layer="server"
      message={error.message}
      trace={
        isResponseWithErrorDetails(error.response)
          ? error.response.data.trace
          : typeof error.response?.data === 'object'
            ? JSON.stringify(error.response.data, null, 2)
            : error.response?.data
      }
    />
  );
};

interface ResponseDataWithErrorDetails {
  message: string;
  trace: string;
}

const isResponseWithErrorDetails = (
  response: AxiosResponse<unknown, any> | undefined,
): response is AxiosResponse<ResponseDataWithErrorDetails> =>
  isObject(response?.data) &&
  'message' in response!.data &&
  typeof response!.data.message === 'string' &&
  'trace' in response!.data &&
  typeof response!.data.trace === 'string';

interface ErrorDisplayInnerProps {
  layer: 'ui' | 'server';
  message: string;
  trace?: string;
}

const ErrorDisplayInner: FC<ErrorDisplayInnerProps> = (props) => {
  const { layer, message, trace } = props;
  const { t } = useTranslation();
  const [messageApi, contextHolder] = Message.useMessage();

  return (
    <Flex vertical gap="middle">
      <StyledHeading style={trace ? undefined : { marginBottom: 40 }}>
        <Tag>{layer.toUpperCase()}</Tag> {message}
      </StyledHeading>
      {contextHolder}
      {trace && (
        <CodeWrapper>
          <StyledButton
            id="copy-stack-trace-to-clipboard"
            shape="circle"
            type="link"
            icon={<Icon.Copy size={18} />}
            onClick={async () => {
              await navigator.clipboard.writeText(trace);
              messageApi.info(t('copied-to-clipboard'));
            }}
          />
          <StyledCodeBlock>{trace}</StyledCodeBlock>
        </CodeWrapper>
      )}
    </Flex>
  );
};

const CodeWrapper = styled.div`
  position: relative;
`;

const StyledHeading = styled.h5`
  color: ${colorRed};
  text-align: center;
`;

const StyledCodeBlock = styled(CodeBlock)`
  font-size: 12px;
  border-block: 1px solid ${colorGray[2]};
  margin-bottom: -1px;
  padding: 2.5rem 2rem 2rem;
  border-radius: 0;
  border-inline-width: 0;
`;

const StyledButton = styled(Button)`
  position: absolute;
  top: 10px;
  right: 10px;
`;
